/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable max-len */
/* eslint-disable-next-line max-len */
import React, {
  useEffect,
  useState,
  useContext,
  useRef,
} from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
import styled from 'styled-components';
import Typography from '@digital-hig/mui/dist/Typography';
// eslint-disable-next-line import/no-extraneous-dependencies
import Select from '@mui/material/Select';
// eslint-disable-next-line import/no-extraneous-dependencies
import MenuItem from '@mui/material/MenuItem';
// eslint-disable-next-line import/no-extraneous-dependencies
import SVGIcon from '@mui/material/SvgIcon';
import SVGLanguage from '@digital-hig/icon/lib/esm/icons/language';
import EnvContext from '../common/envContext';
import TranscriptData from './transcriptData';
import {
  getEnvironmentConstant,
} from '../common/utilities';
import {
  generateCuratedListInfo, generateTutorialInfo,
} from '../common/userUtils';
import {
  COURSE_API_HOST,
  ROUTER_BASENAME,
  ENVIRONMENT,
  LEARNING_MODE_COURSEWARE,
} from '../common/constants';
import {
  pushEvent, formatString, generateUnitInfo, generateModuleInfo, generateCourseInfo, triggerManualLinkClick,
} from '../common/analyticsApi';
import logger from '../common/logger';

const VideoPlayer = ({
  content, curatedData, tutorialData, renderTranscript, setHasVideoPlayed, fullWidth, unitId, moduleUri,
}) => {
  const {
    avmEmbedCode: initialAVMEmbedCode, poster, id, src, captions, name,
  } = content;

  const [avmEmbedCode, setAVMEmbedCode] = useState(initialAVMEmbedCode);
  const playerRef = useRef(null);
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const [played, setPlayed] = useState(false);
  const [transcript, setTranscript] = useState({ data: [], url: '' });
  const [videoState, setVideoState] = useState({
    sources: avmEmbedCode ? [`https://help.autodesk.com/videos/${avmEmbedCode}/video.webm`, `https://help.autodesk.com/videos/${avmEmbedCode}/video.mp4`] : src,
    tracks: captions,
    avmEmbedCode,
    poster: avmEmbedCode ? `https://help.autodesk.com/videos/${avmEmbedCode}/poster` : poster,
  });
  const transcriptAPIHost = getEnvironmentConstant({ key: COURSE_API_HOST, env });
  const contextPath = getEnvironmentConstant({ key: ROUTER_BASENAME, env });
  const [localizationData, setLocalizationData] = useState([]);
  const [selectedVideoLanguage, setSelectedVideoLanguage] = useState('English');

  const trackingPercentages = useRef([0, 25, 50, 75, 90]);

  const isPlaying = (player) => (player && !player?.paused);
  const isCourseware = tutorialData?.learningMode === LEARNING_MODE_COURSEWARE || curatedData?.learningMode === LEARNING_MODE_COURSEWARE;

  const { curatedListInfo } = generateCuratedListInfo({ curatedData });
  const { tutorialInfo } = generateTutorialInfo({ tutorialData });

  const triggerMicrolearningVideoProgressAnalytics = (progress, player) => {
    const videoProgress = (progress / 100).toString();
    let event = {
      event: {
        eventType: 'video-progress',
      },
      tutorialInfo,
      videoInfo: {
        videoId: id,
        videoName: `Tutorial Video:${formatString(name)}`,
        videoLength: player.duration,
        videoFilePath: `https://help.autodesk.com/videos/${avmEmbedCode}/video.webm`,
        videoProgress,
        platform: 'standard html5',
      },
    };
    event = curatedListInfo ? { ...event, ...{ curatedListInfo } } : event;
    pushEvent({ event });
  };

  const triggerCoursewareVideoProgressAnalytics = ({
    courseData, progress = null, videoLength, moduleData,
  }) => {
    if ((courseData || moduleData) && unitId && id && progress !== null && progress < 100) {
      let videoName = 'unknown';

      const unitContent = courseData ? courseData.unitLookup[unitId].content : moduleData.unitLookup[unitId].content;
      const filteredUnitContent = unitContent.filter((item) => item.id === id);

      if (filteredUnitContent.length) {
        videoName = filteredUnitContent[0].name;
      }
      const event = {
        event: {
          eventType: 'video-progress',
        },
        unitInfo: generateUnitInfo({ unitId, courseData, moduleData }),
        videoInfo: {
          videoId: id,
          videoName: `Learning Video:${formatString(videoName)}`,
          videoLength,
          videoFilePath: `https://help.autodesk.com/videos/${avmEmbedCode}/video.webm`,
          videoProgress: `${progress / 100}`,
        },
      };

      if (courseData) {
        event.courseInfo = generateCourseInfo({ courseData });
        event.moduleInfo = generateModuleInfo({ moduleId: courseData.unitLookup[unitId].parentId, courseData });
      }

      if (moduleData) {
        event.moduleInfo = generateModuleInfo({ moduleData });
      }

      pushEvent({ event });
    } else {
      logger.error('Failed to push video progress event');
    }
  };

  const timeUpdateCallback = (event) => {
    const player = event.target;
    const progress = Math.floor((player.currentTime / player.duration) * 100);

    if (isPlaying(player) && trackingPercentages.current.length && progress >= trackingPercentages.current[0]) {
      if (isCourseware) {
        triggerCoursewareVideoProgressAnalytics({
          courseData: curatedData,
          progress: trackingPercentages.current[0],
          videoLength: player.duration,
          moduleData: tutorialData,
        });
      } else {
        triggerMicrolearningVideoProgressAnalytics(trackingPercentages.current[0], player);
      }
      trackingPercentages.current.shift();
    }

    if (isPlaying(player) && !played) {
      // eslint-disable-next-line no-param-reassign
      setHasVideoPlayed(true);
      setPlayed(true);
    }
  };

  const handleChange = (e) => {
    if (videoState.tracks && videoState.tracks.length) {
      const { language } = Array.from(e.target).filter((t) => t.mode === 'showing')[0];
      const curTrack = videoState.tracks.filter((track) => track.label === language);
      setVideoState({ ...videoState, transcriptDataUrl: curTrack[0].src });
    }
  };

  useEffect(() => {
    const playerref = playerRef.current;

    if (videoState.tracks?.length && playerref?.textTracks?.length) {
      playerref.textTracks.addEventListener('change', handleChange);
    } else if (playerref.textTracks.removeEventListener) {
      // Ensure any remaining event listeners are removed when the video is changed
      playerref.textTracks.removeEventListener('change', handleChange);
    }

    return () => {
      if (playerref?.textTracks?.length) {
        playerref.textTracks.removeEventListener('change', handleChange);
      }
    };
  }, [videoState.tracks, playerRef]);

  useEffect(() => {
    if (avmEmbedCode && videoState.transcriptDataUrl === undefined) {
      axios.get(`https://help.autodesk.com/videos/${avmEmbedCode}/data.json`).then((resp) => {
        if (resp.data) {
          if (resp.data.captionLangs && resp.data.captionLangs.length) {
            const capTracks = resp.data.captionLangs.map((langCode) => ({
              default: langCode === 'en', kind: 'captions', label: langCode, src: `https://help.autodesk.com/videos/${avmEmbedCode}/${langCode}.vtt`,
            }));
            const defaultTrack = capTracks.filter((track) => track.default);
            const transcriptDataUrl = defaultTrack.length ? defaultTrack[0].src : capTracks[0].src;
            setVideoState({ ...videoState, tracks: capTracks, transcriptDataUrl });
          }
        }
      });
    }
  }, [avmEmbedCode, videoState]);

  useEffect(() => {
    if (avmEmbedCode && localizationData.length === 0) {
      axios.get(`${transcriptAPIHost}${contextPath}/api/videoloc/v1/${avmEmbedCode}`).then((resp) => {
        if (resp.data && resp.data.length > 1) {
          setLocalizationData(resp.data);
        }
      }).catch((e) => {
        // Middleware returns 200 and empty array when the KPlat service returns 404 because 404 status is returned for empty data
        // Catch all remaining errors here from the middleware
        logger.error('Error in fetching video localization data', e);
      });
    }
  }, [avmEmbedCode, localizationData]);

  useEffect(() => {
    if (videoState.tracks && videoState.tracks.length) {
      const url = `${transcriptAPIHost}${contextPath}/api/vttjson/v1/${encodeURIComponent(videoState.transcriptDataUrl)}`;
      if (transcript.url !== url) {
        axios.get(url).then((resp) => {
          if (resp.data) {
            setTranscript({ data: resp.data, url });
          }
        });
      }
    } else { // When the video language is changed the new video may not have closed captions so we need to clear the transcript data
      setTranscript({ data: [], url: '' });
    }
  }, [videoState.tracks, videoState.transcriptDataUrl]);

  const getEmbedCodeByLanguage = (lang) => {
    const filteredData = localizationData.filter((loc) => loc.title.toLowerCase() === lang.toLowerCase());
    return filteredData.length ? filteredData[0].embedCode : '';
  };

  const selectVideoLanguage = (e) => {
    const selectedEmbedCode = getEmbedCodeByLanguage(e.target.value);
    if (getEmbedCodeByLanguage(selectedVideoLanguage) !== selectedEmbedCode) {
      const updatedVideoState = videoState;
      // Handle updates from default state if avmEmbedCode changes
      delete updatedVideoState.transcriptDataUrl;
      delete updatedVideoState.tracks;
      updatedVideoState.avmEmbedCode = selectedEmbedCode;
      updatedVideoState.sources = [`https://help.autodesk.com/videos/${selectedEmbedCode}/video.webm`, `https://help.autodesk.com/videos/${selectedEmbedCode}/video.mp4`];
      updatedVideoState.poster = `https://help.autodesk.com/videos/${selectedEmbedCode}/poster`;
      setVideoState(updatedVideoState);
      setSelectedVideoLanguage(e.target.value);
      setAVMEmbedCode(selectedEmbedCode);

      triggerManualLinkClick({ linkTitle: `Selected language ${e.target.value} for video ${id}` });
    }
  };

  return (
    <RootElement>
      <Grid container columnSpacing="4" className={fullWidth ? 'fullWidth' : ''}>
        <Grid item lg={fullWidth ? 12 : 8} md={12} sm={12} className="video-player-container">
          <div className="video-player">
            <video
              id={id}
              key={videoState.sources ? videoState.sources[0] : 'default'}
              width="100%"
              ref={playerRef}
              poster={videoState.poster}
              crossOrigin="anonymous"
              controls
              onTimeUpdate={timeUpdateCallback}
              onDurationChange={timeUpdateCallback}
            >
              {videoState.sources ? videoState.sources.map((source) => <source src={source} key={source} />) : <></>}
              {videoState.tracks ? videoState.tracks.map((t) => <track default={t.default} kind={t.kind} src={t.src} srcLang={t.label} label={t.label} key={t.src} />) : <></>}
            </video>
          </div>
          {localizationData.length > 0 && (
            <div className={`localization ${ !!moduleUri && isCourseware ? 'padBottom' : ''}`}>
              <Typography variant="headline-smallest">
                <SVGIcon id="langIcon"><SVGLanguage /></SVGIcon>
                {' '}
                Video language:
                <Select
                  id="languageSelect"
                  onChange={selectVideoLanguage}
                  value={selectedVideoLanguage}
                >
                  {localizationData.map((loc) => (
                    <MenuItem value={loc.title} key={loc.embedCode}>{loc.title}</MenuItem>
                  ))}
                </Select>
              </Typography>
            </div>
          )}

        </Grid>
        {renderTranscript && transcript.data.length !== 0
          && (
            <Grid item lg={4} md={12} sm={12}>
              <TranscriptData transcriptData={transcript.data} player={playerRef} videoId={id} curatedData={curatedData} tutorialData={tutorialData} />
            </Grid>
          )}
      </Grid>
    </RootElement>
  );
};

const RootElement = styled.div`

  .localization {
      padding-top: 16px;
      &.padBottom {
          padding-bottom: 16px;
      }
      #langIcon {
          width: 24px;
          height: 24px;
          padding-right: 0.5em
      }
      .MuiInputBase-root {
          margin-left: 0.5em;
          border-radius: 4px;
          box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.3) inset;
      }
      .MuiSelect-select {
          border-radius: 4px;
          padding-right: 2em !important;
          &:before {
              border-radius: 4px !important; 
          }
      }
  }
  
  .downloadLink {
    padding-top: 8px;
  }
  
  .fullWidth {
    padding: 0px !important
  }
  
  .video-player-container {
    padding-left: 4px !important;
  }


`;

VideoPlayer.propTypes = {
  content: PropTypes.shape({
    avmEmbedCode: PropTypes.string,
    poster: PropTypes.string,
    id: PropTypes.string,
    src: PropTypes.string,
    captions: PropTypes.string,
    name: PropTypes.string,
  }),
  curatedData: PropTypes.shape({
    learningMode: PropTypes.string,
  }),
  tutorialData: PropTypes.shape({
    learningMode: PropTypes.string,
  }),
  renderTranscript: PropTypes.bool,
  setHasVideoPlayed: PropTypes.func,
  fullWidth: PropTypes.bool,
  unitId: PropTypes.string,
  moduleUri: PropTypes.string,
};

VideoPlayer.defaultProps = {
  content: {},
  curatedData: null,
  tutorialData: null,
  renderTranscript: true,
  fullWidth: false,
  unitId: null,
  moduleUri: null,
  setHasVideoPlayed: () => {},
};

export default React.memo(VideoPlayer);
